import type { InboxThreadListItem, InboxThreadsPageView } from "./view-model";

/**
 * Pages through several inboxes as if they were one list.
 *
 * The backend filters threads by a single `alias_id`, so "show me these three
 * inboxes" is one cursor-paginated request per inbox, merged newest-first here.
 * This is a k-way merge with one rule that keeps it honest: never emit a thread
 * while another inbox might still hold a newer one we have not fetched. That is
 * why an inbox whose buffer runs dry is refilled before the merge continues.
 *
 * The result keeps the single-inbox page shape, so the cache updaters for
 * read state and deletion work on merged lists unchanged. Everything needed to
 * continue (each inbox's cursor and its fetched-but-unshown threads) travels in
 * `nextCursor` as JSON; it lives in the query cache, never in a URL.
 */

type SourceState = {
  cursor: string | null;
  /** The backend has no more pages for this inbox. */
  done: boolean;
  /** Fetched, not yet emitted, newest first. */
  buffer: InboxThreadListItem[];
};

type MergeState = Record<string, SourceState>;

export type FetchThreadsPage = (
  aliasId: string,
  cursor: string | null,
) => Promise<InboxThreadsPageView>;

function readState(cursor: string | null, aliasIds: string[]): MergeState {
  const saved = cursor ? (JSON.parse(cursor) as MergeState) : {};
  return Object.fromEntries(
    aliasIds.map((id) => [id, saved[id] ?? { cursor: null, done: false, buffer: [] }]),
  );
}

const timestamp = (thread: InboxThreadListItem) => Date.parse(thread.lastMessageAt) || 0;

export async function fetchMergedThreadsPage({
  aliasIds,
  cursor,
  pageSize,
  fetchPage,
}: {
  aliasIds: string[];
  cursor: string | null;
  pageSize: number;
  fetchPage: FetchThreadsPage;
}): Promise<InboxThreadsPageView> {
  const state = readState(cursor, aliasIds);

  // Fetch the next page for every inbox that has nothing buffered, in parallel.
  const topUp = () =>
    Promise.all(
      aliasIds.map(async (id) => {
        const source = state[id];
        if (source.done || source.buffer.length > 0) return;
        const page = await fetchPage(id, source.cursor);
        source.buffer = page.items;
        source.cursor = page.nextCursor;
        // An empty page that claims more would spin forever; treat it as the end.
        source.done = !page.hasMore || !page.nextCursor || page.items.length === 0;
      }),
    );

  const items: InboxThreadListItem[] = [];
  const emitted = new Set<string>();

  while (items.length < pageSize) {
    // An inbox that is dry but not done may hold something newer than every
    // buffered thread, so it is refilled before anything else is emitted.
    if (aliasIds.some((id) => state[id].buffer.length === 0 && !state[id].done)) {
      await topUp();
      continue;
    }

    let newest: string | null = null;
    for (const id of aliasIds) {
      const head = state[id].buffer[0];
      if (!head) continue;
      if (newest === null || timestamp(head) > timestamp(state[newest].buffer[0])) newest = id;
    }
    if (newest === null) break;

    const thread = state[newest].buffer.shift()!;
    // A conversation that involves two selected inboxes comes back from both.
    if (emitted.has(thread.id)) continue;
    emitted.add(thread.id);
    items.push(thread);
  }

  const hasMore = aliasIds.some((id) => state[id].buffer.length > 0 || !state[id].done);

  return { items, hasMore, nextCursor: hasMore ? JSON.stringify(state) : null };
}
